import React from "react";
import type {
  Control,
  FieldValues,
  Path,
  UseFormClearErrors,
} from "react-hook-form";
import type { Nullable } from "types/utilities";
import type { Country } from "react-phone-number-input/input-mobile";
import type { OtpStatus } from "../stores/otpStore";
import { isValidPhoneNumber } from "react-phone-number-input";
import { PhoneInput } from "@picklebet/ui";
import { useIsPhoneVerificationEnabled } from "@/features/auth/hooks/useIsPhoneVerificationEnabled";
import cx from "classnames";

import type {
  OtpVerificationChannel,
  OtpVerificationPurpose,
  WithOtpFlow,
} from "../utils/types";
import { OtpVerification } from "./OtpVerification";

import * as styles from "./OtpVerification.module.scss";

type PhoneVerificationProps<T extends FieldValues> = WithOtpFlow<{
  purpose: OtpVerificationPurpose;
  channel: OtpVerificationChannel;
  control?: Control<T>;
  destination: string;
  name?: Path<T>;
  label?: string;
  errorMessage?: string;
  clearErrors?: UseFormClearErrors<T>;
  country?: Country;
  placeholder?: string;
  onVerified?: (
    verificationRequestId: string | null,
    phone: string | null,
  ) => void;
  onSessionExpired?: () => void;
  authToken?: Nullable<string>;
  readOnly?: boolean;
  autoSend?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
}>;

export const PhoneVerification = <T extends FieldValues>({
  flow,
  purpose,
  channel,
  control,
  destination,
  name,
  label,
  errorMessage,
  clearErrors,
  disabled,
  placeholder = "Enter your mobile number",
  onVerified,
  onSessionExpired,
  authToken,
  country = "AU",
  readOnly,
  autoSend,
  autoFocus,
}: PhoneVerificationProps<T>) => {
  const { isEnabled: isFFActive } = useIsPhoneVerificationEnabled({
    reportFailures: flow === "signup" && country === "AU",
  });
  const isEnabled = isFFActive && country === "AU";

  const defaultLabel =
    country === "AU" ? "Australian Mobile Number" : "Mobile Number";

  const handleVerificationStatusChange = (
    verificationRequestId: string | null,
    phone: string | null,
  ) => {
    if (verificationRequestId) {
      onVerified?.(verificationRequestId, phone);
    }
  };

  return (
    <OtpVerification
      flow={flow}
      isEnabled={isEnabled}
      destination={destination}
      channel={channel}
      name={name}
      purpose={purpose}
      authToken={authToken}
      onVerified={handleVerificationStatusChange}
      onSessionExpired={onSessionExpired}
      clearErrors={(name) => clearErrors?.(name as Path<T>)}
      disabled={
        disabled ||
        // destination will be a userId for any channel thats not a new signup
        (channel === "SMS" && !isValidPhoneNumber(destination || ""))
      }
      autoSend={autoSend}
    >
      {(RetryButton, otpErrorMessage, isDrawOpen) => {
        return (
          <PhoneInput
            wrapperClassName={cx({
              [styles.isExpanded]: isDrawOpen,
            })}
            autoFocus={autoFocus}
            readOnly={readOnly}
            inputMode={readOnly ? "none" : "numeric"}
            country={country}
            name={name}
            control={control}
            label={label ?? defaultLabel}
            placeholder={placeholder}
            disabled={disabled}
            withCountryCallingCode
            iconContainerClassName={styles.verifyPhoneButtonContainer}
            errorMessage={errorMessage || otpErrorMessage}
            icon={RetryButton}
          />
        );
      }}
    </OtpVerification>
  );
};
